export default {
  // ページタイトルと説明
  title: "Deep Research",
  description: "AIを活用した包括的なリサーチ・分析システム",

  // 共通アクション
  actions: {
    newResearch: "新規リサーチ",
    search: "リサーチを検索...",
    cancel: "キャンセル",
    delete: "削除",
    save: "保存",
    back: "戻る",
    download: "ダウンロード",
    approve: "承認",
    edit: "編集",
    start: "開始",
  },

  // ステータス
  status: {
    all: "すべて",
    analyzing: "初期分析中",
    planning: "プラン生成中",
    planPending: "プラン承認待ち",
    researching: "詳細リサーチ中",
    generating: "レポート生成中",
    completed: "完了",
    error: "エラー",
  },

  // リサーチモード
  researchMode: {
    fast: "高速モード",
    detailed: "詳細モード",
    fastDescription: "高速モード",
    detailedDescription: "詳細モード",
    detailedExplanation:
      "詳細モードでは記事の全文を取得するため、より時間がかかりますが高品質なリサーチ結果を提供します",
  },

  // リスト画面
  list: {
    noResearches: "リサーチがありません",
    noResearchesDescription: "新しいリサーチを開始してみましょう",
    createNewResearch: "新規リサーチを作成",
    statusFilter: "ステータス",
    sourcesCount: "{{count}}件のソース",
    createdAt: "作成",
    completedAt: "完了",
    progress: "進捗",
    saveToTextFile: "テキストファイルに保存",
    downloadReport: "レポートをダウンロード",
    deleteResearch: "リサーチを削除",
    backToList: "リサーチ一覧に戻る",
  },

  // 作成ダイアログ
  create: {
    title: "新しいリサーチを開始",
    queryLabel: "リサーチしたい内容",
    queryPlaceholder: "例：ChatGPTとClaude AIの違いについて詳しくリサーチしてください",
    queryHelperText: "具体的なリサーチ内容を入力してください（10文字以上）",
    researchModeLabel: "リサーチモード",
    maxTimeLabel: "最大リサーチ時間（分）",
    startResearch: "リサーチを開始",
    starting: "開始中...",
    startingMessage: "リサーチを開始しています...しばらくお待ちください。",
  },

  // ダウンロード形式
  downloadFormats: {
    markdown: "Markdown (.md)",
    markdownDescription: "マークダウン形式",
    word: "Word (.docx)",
    wordDescription: "Word文書形式",
    txt: "Text (.txt)",
    txtDescription: "プレーンテキスト",
  },

  // 進捗画面
  progress: {
    loading: "リサーチ情報を読み込んでいます...",
    notFound: "リサーチが見つかりません",
    backToList: "リサーチ一覧に戻る",
    approvePlan: "プランを承認してリサーチを開始",
    approving: "承認中...",
    deleteResearch: "リサーチを削除",
    saveToTextFile: "Notes Nebula で編集 / 公開",
    downloadReport: "レポートをダウンロード",
    researchProgress: "リサーチ進捗",
    progressCompleted: "{{progress}}% 完了",
    searchProgress: "{{current}}/{{total}}回",
    finalReport: "最終レポート",
    collectingInfo: "情報収集中...",
  },

  // タブ
  tabs: {
    researchInfo: "リサーチ情報",
    sources: "収集したソース",
    planReview: "リサーチプランの確認",
    executedPlan: "実行されたリサーチプラン",
    planGenerating: "リサーチプラン生成中",
    initialAnalysis: "初期分析中",
    researchPlan: "リサーチプラン",
    processing: "処理中",
  },

  // リサーチ情報
  info: {
    noData: "リサーチデータがありません",
    query: "リサーチクエリ",
    status: "ステータス",
    researchMode: "リサーチモード",
    progress: "進捗",
    searchProgress: "検索進捗",
    sourcesCount: "ソース数",
    uniqueDomainsCount: "ユニークドメイン数",
    createdAt: "作成日時",
    completedAt: "完了日時",
    currentProcess: "現在の処理",
    researching: "リサーチ中...",
  },

  // プラン編集
  planEdit: {
    title: "プランの修正指示",
    placeholder: "プランの修正指示を入力してください（例：「競合分析の項目を追加して」「技術的詳細をより具体的に」）",
    helperText: "AIが指示を理解してプランを自動修正します。具体的な指示を入力してください。",
    sendInstruction: "修正指示を送信",
    sending: "修正中...",
    editHistory: "編集履歴",
    currentPlan: "現在のリサーチプラン",
    currentPlanEditing: "現在のリサーチプラン（編集中）",
    planTitle: "リサーチプラン",
    approvalMessage: "以下のリサーチプランを確認し、承認してください。必要に応じて編集指示を送信できます。",
    startMessage: "このプランでリサーチを開始しますか？承認後、AI が自動的に情報収集とレポート作成を行います。",
  },

  // プロセス状態
  process: {
    initialAnalysis: "初期分析を実行中",
    planGeneration: "AI がリサーチプランを生成中",
    initialAnalysisDescription: "リサーチ内容の理解と必要な背景情報の収集を行っています...",
    planGenerationDescription: "収集した情報を基に、詳細なリサーチプランを作成しています...",
    analyzing: "リサーチ内容を分析中...",
    generating: "リサーチプランを作成中...",
    finalizing: "リサーチプランを最終化中...",
    understandingQuery: "クエリの理解を深めています...",
    generatingReport: "レポートを生成中...",
    generatingFinalReport: "最終レポートを生成中...",
    buildingPrompt: "レポート生成プロンプトを構築中...",
    researchModeStarting: "リサーチを開始しています",
    searchQuery: "検索中: {{query}}",
    steps: {
      queryAnalysis: "• クエリの詳細分析\n• 必要な背景情報の検索\n• リサーチ範囲の決定",
      planGeneration: "• リサーチ項目の整理\n• リサーチ手順の策定\n• 期待される成果の定義",
    },
    timeEstimate: "💡 この処理には通常1-3分程度かかります。ページを閉じても処理は継続されます。",
  },

  // エラーメッセージ
  errors: {
    loadFailed: "リサーチ一覧の取得に失敗しました",
    queryRequired: "リサーチ内容を入力してください",
    queryTooShort: "リサーチ内容は10文字以上入力してください",
    startFailed: "リサーチの開始に失敗しました",
    deleteFailed: "リサーチの削除に失敗しました",
    downloadFailed: "レポートのダウンロードに失敗しました",
    reportNotFound: "レポートが見つかりません",
    reportFetchFailed: "レポートの取得に失敗しました",
    planCreationFailed: "リサーチプランの作成に失敗しました",
    planEditFailed: "プランの編集に失敗しました",
    finalReportGenerationFailed: "最終レポートの生成に失敗しました",
    errorOccurred: "エラーが発生しました: {{message}}",
    cannotDeleteRunning: "実行中のリサーチは削除できません",
    conversationIdNotFound: "会話IDが見つかりません",
    pdfNotSupported: "PDF形式は現在サポートされていません。Markdownまたはテキスト形式をご利用ください。",
  },

  // 成功メッセージ
  success: {
    deleted: "リサーチを削除しました",
    downloaded: "レポートを{{format}}形式でダウンロードしました",
    planCreated: "リサーチプランが作成されました",
    planEdited: "リサーチプランを編集しました",
    planSaved: "リサーチプランを保存しました",
    researchStarted: "リサーチを開始しました",
    researchCompleted: "リサーチが完了しました",
    planEditStarted: "プランの編集を開始しました",
  },

  // 確認メッセージ
  confirmations: {
    deleteTitle: "削除の確認",
    deleteResearch: "このリサーチを削除しますか？",
    delete: "削除",
    deleting: "削除中...",
  },

  // ツールチップ
  tooltips: {
    saveToTextFile: "テキストファイルに保存",
    downloadReport: "レポートをダウンロード",
    deleteResearch: "リサーチを削除",
  },

  // 日時ラベル
  dateLabels: {
    created: "作成",
    completed: "完了",
    progress: "進捗",
  },

  // 空状態メッセージ
  emptyState: {
    noResearches: "リサーチがありません",
    noResearchesDescription: "新しいリサーチを開始してみましょう",
    createNewResearch: "新規リサーチを作成",
  },

  // リサーチ範囲
  scope: {
    narrow: "狭い範囲",
    medium: "中程度",
    broad: "広範囲",
  },

  // リサーチ深度
  depth: {
    surface: "表面的",
    detailed: "詳細",
    expert: "専門的",
  },

  // 信頼性
  reliability: {
    high: "高信頼性",
    medium: "中信頼性",
    low: "低信頼性",
    unverified: "未検証",
  },
};
